/**
 * NecklaceScrollMockup — founder-only preview page.
 *
 * Route: /demo/necklace-scroll  (not linked from anywhere public)
 *
 * A new scroll-triggered necklace concept intended to sit directly beneath
 * the "MY RICH AUNT" wordmark on the landing hero. Five gemstone beads
 * slide in from the LEFT of the viewport one at a time, following a gentle
 * catenary chain path. The moment each bead lands, a "glisten sweep"
 * (angled semi-transparent white gradient) sweeps across it to simulate
 * high-end jewelry reflection.
 *
 * Interactive controls:
 *   - Auto-plays on first scroll into view
 *   - "Replay animation" button re-mounts the scene for on-demand replay
 */
import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { RotateCcw } from "lucide-react";

import { Navbar } from "../components/Navbar";
import { IMAGES } from "../data/content";
import { useNoIndex } from "../lib/useNoIndex";


/* ─── Palette — exact hex codes provided in the brief.
       Each pearl also gets a small aspect + tilt so the strand reads as
       organic freshwater pearls (rice/semi-round shapes) instead of
       identical plastic beads. */
const BEADS = [
  { key: "orange", hex: "#fd6106", label: "Vibrant Orange-Red", aspect: 1.10, tilt: -3 },
  { key: "gold",   hex: "#fdd006", label: "Bright Gold",        aspect: 1.15, tilt:  2 },
  { key: "royal",  hex: "#0322f4", label: "Royal Blue",         aspect: 1.08, tilt:  0 },
  { key: "rose",   hex: "#fd98d9", label: "Soft Rose",          aspect: 1.12, tilt: -2 },
  { key: "aqua",   hex: "#8ae0f8", label: "Ice Aqua",           aspect: 1.14, tilt:  4 },
];


/* ─── Layout constants ───────────────────────────────────────────── */
// Necklace viewBox is 900x260.  The chain is a gentle quadratic bezier
// (a "smile" shape).  Beads sit at t = 0.10, 0.30, 0.50, 0.70, 0.90 along
// that curve so the whole thing reads as a well-spaced strand.
const VIEW_W = 900;
const VIEW_H = 260;

const beadY = (t) => 40 + 320 * t * (1 - t); // y(t) along the chain quadratic bezier
const beadX = (t) => 40 + 820 * t;
const CHAIN_POINTS = BEADS.map((b, i) => {
  const t = 0.10 + i * 0.20;
  return { ...b, cx: beadX(t), cy: beadY(t), t };
});

const BEAD_RADIUS = 34;      // 68px short-axis; long axis varies per bead
const GLISTEN_DUR = 1.4;
const BEAD_STAGGER = 2.0;    // seconds between successive beads STARTING — long pause, contemplative
const BEAD_DURATION = 2.6;   // each pearl's glide duration — deliberate, not snappy


/* ─── One freshwater pearl bead ─────────────────────────────────── */
const Bead = ({ bead, index, reduce }) => {
  const [landed, setLanded] = useState(false);

  // ARRIVAL ORDER — mirrors how a necklace is actually threaded:
  //   the first pearl added is pushed to the far END of the string
  //   (rightmost). Each new pearl comes in and slots to its LEFT.
  //   So on-screen order of arrival is: aqua → rose → royal → gold → orange.
  const arrivalOrder = BEADS.length - 1 - index;   // 0..4 (0 lands first)
  const delay = 0.4 + arrivalOrder * BEAD_STAGGER;

  // Freshwater pearl look:
  //   • Two stacked radial gradients — a small warm highlight (top-left)
  //     and a much softer "orient" glow (bottom-right) — sit above a
  //     linear body gradient that gently deepens toward the bottom.
  //   • NO dark rim / no inset shadow (which was reading as an iris).
  //   • Shape is a subtle oval (per-bead aspect + tilt) so the strand
  //     looks like real freshwater pearls, not identical plastic beads.
  const shortAxis = BEAD_RADIUS * 2;
  const longAxis  = shortAxis * bead.aspect;

  // Slide-in origin — every pearl starts at container-x ≈ -60px (just
  // off the LEFT edge of the necklace canvas) and glides all the way
  // to its resting spot. Rightmost pearl (aqua) travels the LONGEST
  // distance, leftmost (orange) travels the shortest — exactly like
  // pushing beads one at a time down the string.
  const slideStartX = -(bead.cx + 60);

  const style = {
    left: bead.cx - longAxis / 2,
    top:  bead.cy - shortAxis / 2,
    width:  longAxis,
    height: shortAxis,
    transform: `rotate(${bead.tilt}deg)`,
    borderRadius: "50%",
    background: `
      radial-gradient(ellipse 44% 34% at 32% 26%,
        rgba(255,255,255,0.90) 0%,
        rgba(255,255,255,0.20) 45%,
        transparent 75%),
      radial-gradient(ellipse 55% 40% at 70% 78%,
        rgba(255,255,255,0.18) 0%,
        transparent 65%),
      linear-gradient(155deg,
        ${bead.hex} 30%,
        rgba(0,0,0,0.10) 100%),
      ${bead.hex}
    `,
    boxShadow: `
      0 20px 40px -14px rgba(0,0,0,0.55),
      0 4px 10px -2px rgba(0,0,0,0.30)
    `,
  };

  return (
    <motion.div
      data-testid={`gem-bead-${bead.key}`}
      className="absolute overflow-hidden"
      style={style}
      initial={reduce ? false : { x: slideStartX, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{
        delay,
        duration: BEAD_DURATION,
        opacity: { delay, duration: Math.min(0.6, BEAD_DURATION / 3) },
        ease: [0.16, 0.84, 0.24, 1], // smooth deceleration — glides, settles, doesn't snap
      }}
      onAnimationComplete={() => setLanded(true)}
    >
      {/* Glisten sweep — sails across the pearl a beat AFTER it lands,
          so the eye reads: settle first, then catch the light. */}
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-[-20%] left-[-40%] w-[180%]"
        style={{
          background:
            "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.7) 50%, transparent 70%)",
          mixBlendMode: "screen",
        }}
        initial={{ x: "-100%", opacity: 0 }}
        animate={landed && !reduce ? { x: "100%", opacity: [0, 1, 1, 0] } : undefined}
        transition={{
          delay: 0.35,
          duration: GLISTEN_DUR,
          ease: "easeInOut",
          times: [0, 0.15, 0.85, 1],
        }}
      />
    </motion.div>
  );
};


/* ─── The animated scene ─────────────────────────────────────────── */
const NecklaceScene = ({ replayKey }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, amount: 0.4 });
  const reduce = useReducedMotion();

  return (
    <div
      ref={ref}
      className="relative mx-auto w-full max-w-[900px]"
      data-testid="necklace-scene"
    >
      {/* Chain — gentle metallic hairline */}
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className="block w-full"
        aria-hidden="true"
      >
        <defs>
          {/* Gold hairline gradient — brighter in the middle, deeper at the ends */}
          <linearGradient id="chain-gold" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor="#8B6A2E" />
            <stop offset="20%"  stopColor="#D9B159" />
            <stop offset="50%"  stopColor="#F4D48A" />
            <stop offset="80%"  stopColor="#D9B159" />
            <stop offset="100%" stopColor="#8B6A2E" />
          </linearGradient>
        </defs>

        {/* Chain — animate stroke length so it draws in first,
            paired with the beads landing on top of it */}
        <motion.path
          d={`M 40 40 Q 450 200, 860 40`}
          fill="none"
          stroke="url(#chain-gold)"
          strokeWidth="1.8"
          strokeLinecap="round"
          initial={reduce ? false : { pathLength: 0, opacity: 0 }}
          animate={inView ? { pathLength: 1, opacity: 1 } : undefined}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
        />
      </svg>

      {/* Beads layer — absolutely positioned over the SVG viewBox in %
          Because the SVG scales responsively with its aspect ratio, we
          nest an absolute-positioned box whose coordinate system also
          scales — using the same 900x260 viewBox math. */}
      <div
        className="pointer-events-none absolute inset-0"
        key={replayKey /* remount to reset animations on replay */}
      >
        <div
          className="relative h-full w-full"
          style={{
            // Give the child divs a coordinate space of 900x260 CSS units
            // that scales with the SVG.  We do this via CSS aspect-ratio
            // matching + percentage positioning inside <Bead>.
          }}
        >
          {inView &&
            CHAIN_POINTS.map((bead, i) => (
              <BeadWrapper key={bead.key} bead={bead} index={i} reduce={reduce} />
            ))}
        </div>
      </div>
    </div>
  );
};


/* ─── Bead positioned via percentage so it scales with the SVG ─── */
const BeadWrapper = ({ bead, index, reduce }) => {
  // Convert viewBox coords → % so the bead scales with its parent
  const leftPct = (bead.cx / VIEW_W) * 100;
  const topPct  = (bead.cy / VIEW_H) * 100;
  return (
    <div
      className="absolute"
      style={{
        left: `${leftPct}%`,
        top:  `${topPct}%`,
        transform: "translate(-50%, -50%)",
      }}
    >
      <Bead
        bead={{ ...bead, cx: 0, cy: 0 }}
        index={index}
        reduce={reduce}
      />
    </div>
  );
};


/* ─── Page ────────────────────────────────────────────────────────── */

export default function NecklaceScrollMockup() {
  useNoIndex();
  const [replayKey, setReplayKey] = useState(0);

  // Force autoplay on first mount — some browsers don't fire useInView
  // on initial paint at the top of the page (element is in view but the
  // observer's callback hasn't tripped yet).  Bumping replayKey after
  // paint reliably kicks off the scene.
  useEffect(() => {
    const t = setTimeout(() => setReplayKey((k) => k + 1), 60);
    return () => clearTimeout(t);
  }, []);

  return (
    <main
      data-testid="necklace-scroll-page"
      className="relative min-h-screen bg-sand text-ink"
    >
      <Navbar
        onStart={() => {}}
        primaryLabel="Preview the platform"
        secondaryLabel={null}
      />

      <section className="mx-auto max-w-6xl px-6 pb-8 pt-32 lg:px-8 lg:pt-44">
        <span className="inline-flex items-center gap-2 rounded-full bg-cobalt/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-cobalt">
          Necklace concept · sub-hero band
        </span>
        <h1 className="mt-4 max-w-3xl font-serif text-5xl leading-[0.95] tracking-tight text-ink lg:text-6xl">
          Beads slide onto the chain, one by one, with a{" "}
          <em className="italic text-tangerine">glisten sweep</em> on landing.
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-olive">
          Scroll-triggered — the scene starts when this band enters the viewport.
          Beads travel in from the left of the screen sequentially. The moment
          each one lands, a semi-transparent white gradient sweeps across it
          left-to-right, simulating high-end jewelry reflection.
        </p>
      </section>

      {/* THE ANIMATION — sits on a warm charcoal canvas so the gem colors pop.
          This is the exact treatment intended for the sub-hero band. */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse at 50% 30%, #2A2622 0%, #1B1815 55%, #0F0D0B 100%)",
        }}
        data-testid="necklace-canvas"
      >
        {/* Faint MRA wordmark above the necklace — proxy for how the band
            will sit beneath the "MY RICH AUNT" hero lockup. */}
        <div className="mx-auto max-w-6xl px-6 pt-16 lg:px-8 lg:pt-24">
          <img
            src={IMAGES.logoWordmark}
            alt="MY RICH AUNT"
            className="mx-auto block h-auto w-40 opacity-90 invert lg:w-56"
          />
        </div>

        <div className="mx-auto max-w-6xl px-6 pb-24 pt-10 lg:px-8 lg:pb-32">
          <NecklaceScene replayKey={replayKey} />
        </div>

        {/* Palette caption — small legend so the founder can verify hex codes */}
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-6 pb-16 lg:px-8">
          {BEADS.map((b) => (
            <div key={b.key} className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white/50">
              <span
                className="h-3 w-3 rounded-full"
                style={{ backgroundColor: b.hex, boxShadow: `0 0 12px ${b.hex}88` }}
              />
              <span>{b.label}</span>
              <span className="font-mono text-white/35">{b.hex}</span>
            </div>
          ))}
        </div>

        {/* Replay button */}
        <div className="mx-auto flex max-w-6xl justify-center px-6 pb-20 lg:px-8">
          <button
            type="button"
            data-testid="necklace-replay-btn"
            onClick={() => setReplayKey((k) => k + 1)}
            className="group inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3 text-[13px] font-semibold uppercase tracking-[0.22em] text-white/90 backdrop-blur transition hover:bg-white/12 hover:text-white"
          >
            <RotateCcw
              className="h-4 w-4 transition-transform group-hover:-rotate-180"
              strokeWidth={2}
            />
            Replay animation
          </button>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-16 lg:px-8">
        <h2 className="font-serif text-3xl leading-tight text-ink">Notes.</h2>
        <ul className="mt-6 space-y-3 text-[15px] leading-relaxed text-olive">
          <li>
            <strong>Threading order:</strong> pearls land right-to-left along
            the chain — aqua first (rightmost), then rose, royal, gold, orange
            last (leftmost). Each pearl originates just off the LEFT edge and
            glides all the way to its resting position, so the rightmost pearl
            visibly travels the longest distance.
          </li>
          <li>
            <strong>Timing:</strong> pearls arrive at{" "}
            <code className="rounded bg-ink/8 px-1.5 py-0.5 text-[13px]">3.0s, 5.0s, 7.0s, 9.0s, 11.0s</code>{" "}
            (stagger {BEAD_STAGGER}s · glide {BEAD_DURATION}s each · deep-decel ease). Total sequence ≈ 11.5s.
          </li>
          <li>
            <strong>Pearl body:</strong> layered radial gradients (main
            highlight top-left, soft orient bottom-right) over a linear body
            gradient. No dark rim, no inset shadow — reads as freshwater
            pearl luster, not gemstone iris.
          </li>
          <li>
            <strong>Shape:</strong> each pearl is a slightly-off oval
            (aspect 1.08–1.15) with a per-bead tilt of ±4° so the strand
            reads as organic freshwater pearls, not identical plastic beads.
          </li>
          <li>
            <strong>Glisten sweep:</strong> angled 115° linear-gradient,{" "}
            <code className="rounded bg-ink/8 px-1.5 py-0.5 text-[13px]">
              translateX -100% → 100%
            </code>{" "}
            over {GLISTEN_DUR}s, screen-blended so it reads as light not paint.
          </li>
          <li>
            <strong>Canvas:</strong> warm charcoal radial gradient (#2A2622 →
            #0F0D0B) so the palette pops. Ready to drop into the landing page's
            sub-hero slot as a full-width band.
          </li>
          <li>
            <strong>Reduced motion:</strong> honors{" "}
            <code className="rounded bg-ink/8 px-1.5 py-0.5 text-[13px]">
              prefers-reduced-motion
            </code>{" "}
            — pearls and chain appear immediately with no glide/glisten.
          </li>
        </ul>
      </section>
    </main>
  );
}
